import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert } from 'reactstrap';

import { useAppDispatch, useAppSelector } from 'app/config/store';
import { activateAction, fetchConsentInfo, sendConsentEmail, reset } from './activate.reducer';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import AmyIcon from 'app/components/common/cards/chat-card/amy-icons';
import './activate.scss';

export const ActivatePage = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const invitedOrgId = parseInt(localStorage.getItem('invitedOrgId'));
  const invitation = localStorage.getItem('invitation');
  const token = localStorage.getItem('token');

  const key = searchParams.get('key');

  const { activationSuccess, activationFailure, consentInfo, consentInfoLoading, consentInfoError, consentSent, consentSendFailure } = useAppSelector(
    state => state.activate
  );
  const consentPending = useAppSelector(state => state.authentication.consentPending);
  const maskedParentEmail = useAppSelector(state => state.authentication.maskedParentEmail);

  useEffect(() => {
    if (key) {
      dispatch(fetchConsentInfo(key));
    }
    return () => {
      dispatch(reset());
    };
  }, []);

  const handleActivate = () => {
    dispatch(activateAction(key));
  };

  const handleRequestConsent = () => {
    dispatch(sendConsentEmail(key));
  };

  const getBaseDomain = (url: string) => url.split('/')[2];

  const setBaseUrl = (domain: string): string => {
    switch (domain) {
      case 'qa.reachamy.com':
        return 'https://admin.qa.reachamy.com';
      case 'stage.reachamy.com':
        return 'https://admin.stage.reachamy.com';
      case 'reachamy.com':
        return 'https://admin.reachamy.com';
      default:
        return '';
    }
  };

  const domain = getBaseDomain(window.location.href);
  const baseUrl = setBaseUrl(domain);

  const goToAdminPortal = (e: any) => {
    e.preventDefault();
    localStorage.removeItem('invitation');
    localStorage.removeItem('token');
    localStorage.removeItem('invitedOrgId');
    window.location.href = ` ${baseUrl}/invite?invitation=accept&token=${token}`;
  };

  const homeUrl = invitedOrgId ? `/?invited=true&org=${invitedOrgId}` : '/?action=login';

  const successAlert = (
    <Alert color="success">
      <strong>Your user account has been activated.</strong> Please
      {invitation === 'accept' && token ? (
        <a href={`${baseUrl}/invite?invitation=accept&token=${token}`} onClick={goToAdminPortal} className="alert-link mx-1">
          Sign in
        </a>
      ) : (
        <Link to={homeUrl} className="alert-link mx-1">
          Sign in
        </Link>
      )}
    </Alert>
  );

  const failureAlert = (
    <Alert color="danger">
      <strong>Your user could not be activated.</strong> Please use the registration form to sign up.
    </Alert>
  );

  if (consentInfoLoading) {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={{ height: '75vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (consentInfoError && !consentPending) {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={{ height: '75vh' }}>
        <Box className="text-center" sx={{ width: '50%' }}>
          <AmyIcon sx={{ width: '100%', mb: 2, height: '100px' }} />
          <Typography variant="h4" gutterBottom>
            Link Already Used
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            This activation link has already been used.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            If you&apos;ve already activated your account, please{' '}
            <Link to="/?action=login" className="alert-link" style={{ color: 'inherit' }}>
              sign in
            </Link>{' '}
            to check your account status.
          </Typography>
        </Box>
      </Box>
    );  
  }

  if (consentPending) {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={{ height: '75vh' }}>
        <Box className="text-center" sx={{ width: '50%' }}>
          <AmyIcon sx={{ width: '100%', mb: 2, height: '100px' }} />
          <Typography variant="h4" gutterBottom>
            Consent Request Already Sent
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Your account is active and a consent request has already been sent to{' '}
            {maskedParentEmail ? <strong>{maskedParentEmail}</strong> : 'your parent or guardian'}.
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Please ask your parent or guardian to check their inbox and approve your account.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (consentInfo?.isMinor) {
    return (
      <Box className="d-flex align-items-center justify-content-center" sx={{ height: '75vh' }}>
        <Box className="text-center" sx={{ width: '50%' }}>
          <AmyIcon sx={{ width: '100%', mb: 2, height: '100px' }} />
          <Typography variant="h3" gutterBottom>
            One more step!
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Users aged 16 and 17 need a parent or guardian&apos;s approval to join ReachAMY.
          </Typography>
          {consentInfo.maskedParentEmail && (
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              A consent request will be sent to: <strong>{consentInfo.maskedParentEmail}</strong>
            </Typography>
          )}
          <Box textAlign="center" pt={2}>
            {consentSent ? (
              <Alert color="success">
                <strong>Request sent!</strong> Your account will be activated after your parent approves.
              </Alert>
            ) : (
              <>
                <Button
                  onClick={handleRequestConsent}
                  variant="contained"
                  disableElevation
                  size="small"
                  sx={{
                    textTransform: 'capitalize',
                    backgroundColor: 'primary.main',
                    width: '260px',
                    borderRadius: '10px',
                    '&:hover': { backgroundColor: 'primary.main', boxShadow: 'none' },
                  }}
                >
                  Request Parent Consent
                </Button>
                {consentSendFailure && (
                  <Alert color="danger" className="mt-2">
                    <strong>Failed to send consent request.</strong> Please try again.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="d-flex align-items-center justify-content-center" sx={{ height: '75vh' }}>
      <Box className="text-center" sx={{ width: '50%' }}>
        <AmyIcon sx={{ width: '100%', mb: 2, height: '100px' }} />
        <Typography variant="h3" gutterBottom>
          Activate your account !
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Welcome to ReachAMY! We&apos;re thrilled to have you as part of our community. To begin your journey, just click the button below to
          activate your account
        </Typography>
        <Box textAlign="center" pt={2}>
          <Button
              onClick={handleActivate}
              variant="contained"
              disableElevation
              size="small"
              sx={{
                textTransform: 'capitalize',
                backgroundColor: 'primary.main',
                width: '260px',
                borderRadius: '10px',
                '&:hover': { backgroundColor: 'primary.main', boxShadow: 'none' },
              }}
            >
              Activate
            </Button>
          {activationSuccess ? successAlert : undefined}
          {activationFailure ? failureAlert : undefined}
        </Box>
      </Box>
    </Box>
  );
};

export default ActivatePage;
